import { useState, useEffect, useCallback, useRef } from "react";
import { dismissModal } from "@/utils/modalClose";
import { createPortal } from "react-dom";
import {
  IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon, IonSpinner, IonNote,
  IonCard, IonList, IonItem, IonLabel, IonRadioGroup, IonRadio,
} from "@ionic/react";
import { closeOutline, trashOutline } from "ionicons/icons";
import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement, PaymentRequestButtonElement } from "@stripe/react-stripe-js";
import { useCardForm, StripeMark, CardBrandIcon, cardName, cardNote } from "./CardForm";
import { userHeaders } from "@/utils/userAuth";
import { toast } from "@/utils/toast";
import { confirmAlert } from "@/utils/confirmAlert";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BRANDS = { visa: "Visa", mastercard: "Mastercard", amex: "American Express", discover: "Discover", diners: "Diners Club", jcb: "JCB", unionpay: "UnionPay" };
const brandName = (b) => BRANDS[String(b || "").toLowerCase()] || (b ? String(b).charAt(0).toUpperCase() + String(b).slice(1) : "Card");
const WALLETS = { apple_pay: "Apple Pay", google_pay: "Google Pay", link: "Link", samsung_pay: "Samsung Pay" };
const cardTitle = (c) => `${brandName(c.brand)} •••• ${c.last4}${c.wallet && WALLETS[c.wallet] ? ` · ${WALLETS[c.wallet]}` : ""}`;

// Billing → account row → Payment methods. The document-checkout modal
// chrome (portalled, slide-up, full screen on phones), holding two cards:
// the saved cards with a radio that picks the default (what renewals
// charge), and the Stripe card form to add another, with Apple Pay / Google
// Pay above it on devices that have a wallet (a wallet card is saved through
// the same setup intent and charged at renewals like any card). Mount it
// fresh per open.
export default function PaymentMethodsModal({ onClose: onCloseNow }) {
  const onClose = (e) => dismissModal(onCloseNow, e); // animated exit, then the real close

  const stripe = useStripe();
  const elements = useElements();
  const isMobile = window.innerWidth < 768;
  const [cards, setCards] = useState(null); // null while loading
  const [defaultId, setDefaultId] = useState(null);
  const [busy, setBusy] = useState(null); // "add" | "default" | a card id being removed
  const [error, setError] = useState("");
  const { fields, firstError, billingAddress, reset } = useCardForm({ disabled: busy === "add" });
  const userInfo = (() => { try { return JSON.parse(localStorage.getItem("userInfo") || "null") || {}; } catch { return {}; } })();
  const [paymentRequest, setPaymentRequest] = useState(null); // Apple Pay / Google Pay, when the device has one
  const latest = useRef({}); // the wallet handler reads the latest state without re-creating the request

  const api = async (path, method = "GET", body) => {
    const res = await fetch(`${BACKEND_URL}${path}`, { method, headers: userHeaders(body ? { "Content-Type": "application/json" } : {}), body: body ? JSON.stringify(body) : undefined });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) throw new Error(data.detail || data.error || "Something went wrong");
    return data;
  };
  const load = useCallback(async () => {
    try {
      const d = await api("/api/stripe/payment-methods");
      setCards(d.cards || []);
      setDefaultId(d.defaultId || null);
    } catch (err) {
      setCards([]);
      setError(err.message);
    }
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  // Apple Pay / Google Pay: the wallet hands us a payment method, which the
  // setup intent saves to the customer (nothing is charged now, hence the
  // pending zero total in the wallet sheet).
  useEffect(() => {
    if (!stripe) return undefined;
    let pr;
    try {
      pr = stripe.paymentRequest({ country: "US", currency: "usd", total: { label: "MintSlip: save a card", amount: 0, pending: true }, requestPayerName: true, requestPayerEmail: true });
    } catch { return undefined; }
    let cancelled = false;
    pr.canMakePayment().then((ok) => { if (!cancelled && ok) setPaymentRequest(pr); }).catch(() => {});
    pr.on("paymentmethod", async (ev) => {
      const { saveWalletCard } = latest.current;
      await saveWalletCard(ev);
    });
    return () => { cancelled = true; };
  }, [stripe]);

  const finishSave = async (pmId) => {
    // the first card becomes the default straight away
    if (pmId && (cards || []).length === 0) await api(`/api/stripe/payment-methods/${pmId}/default`, "PUT");
    toast.success("Card added");
    await load();
  };
  latest.current.saveWalletCard = async (ev) => {
    setError(""); setBusy("add");
    try {
      const d = await api("/api/stripe/setup-intent", "POST", {});
      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(d.clientSecret, { payment_method: ev.paymentMethod.id }, { handleActions: false });
      if (confirmError) { ev.complete("fail"); throw new Error(confirmError.message || "That card could not be saved. Please try again."); }
      ev.complete("success");
      let si = setupIntent;
      if (si && si.status === "requires_action") {
        const followUp = await stripe.confirmCardSetup(d.clientSecret);
        if (followUp.error) throw new Error(followUp.error.message || "Card authentication failed.");
        si = followUp.setupIntent;
      }
      await finishSave(si && si.payment_method && (si.payment_method.id || si.payment_method));
    } catch (e) {
      setError(e.message || "That card could not be saved. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const makeDefault = async (id) => {
    if (!id || id === defaultId) return;
    const prev = defaultId;
    setDefaultId(id); setBusy("default");
    try {
      await api(`/api/stripe/payment-methods/${id}/default`, "PUT");
      const picked = (cards || []).find((c) => c.id === id);
      toast.success(picked ? `Default card updated to ${cardName(picked)}` : "Default card updated");
      await load();
    } catch (err) {
      setDefaultId(prev);
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };
  const remove = async (c) => {
    if (!(await confirmAlert({ header: "Remove this card?", message: `${brandName(c.brand)} ending in ${c.last4} is removed from your account.`, confirmText: "Remove" }))) return;
    setBusy(c.id);
    try {
      await api(`/api/stripe/payment-methods/${c.id}`, "DELETE");
      toast.success("Card removed");
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };
  const addCard = async () => {
    setError("");
    if (!stripe || !elements) { setError("The card form is still loading. Please try again."); return; }
    const err = firstError();
    if (err) { setError(err); return; }
    setBusy("add");
    try {
      const d = await api("/api/stripe/setup-intent", "POST", {});
      const result = await stripe.confirmCardSetup(d.clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
          billing_details: { name: userInfo.name || undefined, email: userInfo.email || undefined, address: billingAddress() },
        },
      });
      if (result.error) throw new Error(result.error.message || "That card could not be saved. Please try again.");
      const pm = result.setupIntent?.payment_method;
      [CardNumberElement, CardExpiryElement, CardCvcElement].forEach((E) => { try { elements.getElement(E)?.clear(); } catch { /* already gone */ } });
      reset();
      await finishSave(pm && (pm.id || pm));
    } catch (e) {
      setError(e.message || "That card could not be saved. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  return createPortal(
    <div className="modal-backdrop pm-modal" style={{ position: "fixed", inset: 0, zIndex: 10010, background: isMobile ? "var(--ion-background-color, #f2f2f7)" : "rgba(0,0,0,0.55)", display: "flex", alignItems: isMobile ? "stretch" : "center", justifyContent: isMobile ? "stretch" : "center" }}>
      <div className="modal-slide-up" style={{ background: "var(--ion-background-color, #f2f2f7)", color: "var(--ion-text-color)", display: "flex", flexDirection: "column", width: "100%", maxWidth: isMobile ? "100%" : 600, height: isMobile ? "100%" : "auto", maxHeight: isMobile ? "100%" : "90vh", overflow: "hidden" }}>
        <IonHeader>
          <IonToolbar style={{ "--background": "var(--ion-card-background)", "--color": "var(--ion-text-color)" }}>
            <IonButtons slot="start">
              <IonButton fill="clear" shape="round" onClick={onClose} disabled={busy === "add"} aria-label="Close">
                <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1.8em", color: "var(--ion-text-color)" }}>
                  <IonIcon icon={closeOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                </span>
              </IonButton>
            </IonButtons>
            <IonTitle style={{ fontWeight: 700 }}>Payment methods</IonTitle>
          </IonToolbar>
        </IonHeader>

        <div className="pm-body">
          {/* saved cards, the default picked with a radio */}
          <IonCard className="pm-card">
            <div className="pm-card-head"><StripeMark /><span>Your cards</span></div>
            {cards === null ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><IonSpinner name="crescent" /></div>
            ) : cards.length === 0 ? (
              <p className="pm-empty">No cards saved yet. Add one below.</p>
            ) : (
              <IonRadioGroup value={defaultId || ""} onIonChange={(e) => makeDefault(e.detail.value)}>
                <IonList lines="full">
                  {cards.map((c, i) => (
                    <IonItem key={c.id} lines={i === cards.length - 1 ? "none" : undefined} className="pm-row">
                      <CardBrandIcon slot="start" brand={c.brand} wallet={c.wallet} />
                      <IonLabel>
                        {cardName(c)}
                        <p>{cardNote(c, c.id === defaultId)}</p>
                      </IonLabel>
                      <IonButton slot="end" fill="clear" color="danger" size="small" disabled={busy === c.id} onClick={() => remove(c)} aria-label={`Remove ${brandName(c.brand)} ending in ${c.last4}`}>
                        <IonIcon slot="icon-only" icon={trashOutline} />
                      </IonButton>
                      <IonRadio slot="end" value={c.id} disabled={busy === "default"} aria-label={`Make ${brandName(c.brand)} ending in ${c.last4} the default`} />
                    </IonItem>
                  ))}
                </IonList>
              </IonRadioGroup>
            )}
          </IonCard>

          {/* add a card: the same Stripe form as the checkout */}
          <IonCard className="pm-card">
            <div className="pm-card-head"><StripeMark /><span>Add a card</span></div>
            <div className="pm-form">
              {paymentRequest && (
                <>
                  <PaymentRequestButtonElement options={{ paymentRequest, style: { paymentRequestButton: { theme: "dark", height: "44px" } } }} />
                  <div className="pm-or">
                    <span />
                    <em>or add a card</em>
                    <span />
                  </div>
                </>
              )}
              {fields}
              {error && <IonNote color="danger" className="checkout-error">{error}</IonNote>}
              <IonButton expand="block" color="success" className="checkout-pay" onClick={addCard} disabled={busy === "add"}>
                {busy === "add" ? <IonSpinner name="crescent" /> : "Add card"}
              </IonButton>
            </div>
          </IonCard>
        </div>
      </div>
    </div>,
    document.querySelector("ion-app") || document.body
  );
}
